import './CustomerSettingsManager.scss';
import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import { Button, Form, FormInstance, Input, InputNumber } from 'antd';
import { coreActions, Customer } from '@sonar-software/sonar-core';
import React, { useCallback, useEffect, useRef } from 'react';
import { useAction, useSonarSelector } from '@hooks';

const patchCustomerSelectors = createAsyncSelectors((state) => state.core.customers.byId.patch);

export interface CustomerSettingsManagerProps {
	id?: string;
	customer?: Customer | null;
	searchDetails?: ShardSearchEntryDetails;
}

export interface SalesforceAccountIdFormValues {
	salesforceAccountId?: string;
}
export interface TimelineRetentionPeriodFormValues {
	timelineRetentionPeriod?: number;
}

export const CustomerSettingsManager: React.FC<CustomerSettingsManagerProps> = (props) => {
	const { id, customer, searchDetails } = props;

	const formRefSalesforceAccountId = useRef<FormInstance<SalesforceAccountIdFormValues>>(null);
	const formRefTimelineRetentionPeriod = useRef<FormInstance<TimelineRetentionPeriodFormValues>>(null);

	const patchCustomer = useAction(coreActions.requestPatchCoreCustomersById);
	const isPatchingCustomer = useSonarSelector(patchCustomerSelectors.isRequested);

	const handleSubmitSalesforceAccountId = useCallback(
		(values: SalesforceAccountIdFormValues) => {
			if (searchDetails) {
				const { customerId, shardName } = searchDetails;

				patchCustomer({
					customerId: customerId,
					salesforceAccountId: values.salesforceAccountId ?? null,
					shardName: shardName
				});
			}
		},
		[ patchCustomer, searchDetails ]
	);

	const handleSubmitTimelineRetentionPeriod = useCallback(
		(values: TimelineRetentionPeriodFormValues) => {
			if (searchDetails) {
				const { customerId, shardName } = searchDetails;

				patchCustomer({
					customerId: customerId,
					shardName: shardName,
					timelineRetentionPeriod: values.timelineRetentionPeriod ?? null
				});
			}
		},
		[ patchCustomer, searchDetails ]
	);

	useEffect(() => {
		if (customer) {
			formRefSalesforceAccountId.current?.resetFields();
			formRefTimelineRetentionPeriod.current?.resetFields();
		}
	}, [ customer ]);

	return (
		<div
			className='customer-settings-manager'
			id={ id }>
			<div className='h2'>{ ADMIN_DASHBOARD_COPY.CUSTOMER_SETTINGS }</div>
			<Form
				className='inline-form-with-overhead-labels'
				colon={ false }
				initialValues={ { salesforceAccountId: customer?.salesforceAccountId } }
				layout='inline'
				onFinish={ handleSubmitSalesforceAccountId }
				ref={ formRefSalesforceAccountId }>
				<Form.Item
					label={ ADMIN_DASHBOARD_COPY.SALESFORCE_ACCOUNT_ID }
					name='salesforceAccountId'
					rules={ [
						{
							message: ADMIN_DASHBOARD_COPY.INVALID_SALESFORCE_ACCOUNT_ID,
							pattern: /(^[a-zA-Z0-9]{15}$)|(^[a-zA-Z0-9]{18}$)/u,
							type: 'string'
						}
					] }>
					<Input />
				</Form.Item>
				<Button
					htmlType='submit'
					loading={ isPatchingCustomer }
					type='primary'>
					{ ADMIN_DASHBOARD_COPY.SUBMIT }
				</Button>
			</Form>
			<Form
				className='inline-form-with-overhead-labels'
				colon={ false }
				initialValues={ { timelineRetentionPeriod: customer?.timelineRetentionPeriod } }
				layout='inline'
				onFinish={ handleSubmitTimelineRetentionPeriod }
				ref={ formRefTimelineRetentionPeriod }>
				<Form.Item
					label={ ADMIN_DASHBOARD_COPY.TIMELINE_RETENTION_PERIOD }
					name='timelineRetentionPeriod'>
					<InputNumber
						max={ 1825 }
						min={ 0 } />
				</Form.Item>
				<Button
					htmlType='submit'
					loading={ isPatchingCustomer }
					type='primary'>
					{ ADMIN_DASHBOARD_COPY.SUBMIT }
				</Button>
			</Form>
		</div>
	);
};
